import {
  filledByType,
  ImmutableChat,
  ITemplateFormData,
  TEMPLATES,
} from "@home-health-notify/domain";
import { ISubmitSocketResponse } from "@home-health-notify/models";
import { Typography } from "@mui/material";
import Tab from "@mui/material/Tab";
import {
  useFetchTemplate,
  useFetchTemplates,
  useTranslation,
} from "hhn-commons";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import TabPanel from "../../common/tabs/TabPanel";
import ProgressLoader from "../../loader/ProgressLoader";
import { TemplateTabLabel, TemplateTabsStyle } from "./Template.style";
import TemplatePanel from "./TemplatePanel";
import { TemplateTabsContainer } from "./TemplateTabs.style";

const TemplateTabs = ({
  onDialogClose,
  selectedRole,
  chat,
}: {
  onDialogClose: () => void;
  selectedRole?: filledByType;
  chat: ImmutableChat;
}) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "chat" });
  const { enqueueSnackbar } = useSnackbar();
  const templates = chat && chat.templates;
  const [selectedTemplateKey, setSelectedTemplateKey] =
    React.useState<keyof typeof TEMPLATES>();
  const { isTemplateLoading, templatesInfoMap } = useFetchTemplates(
    selectedRole,
    chat,
  );
  const { handleSubmit } = useFetchTemplate(selectedTemplateKey ?? "SOC", chat);
  let selectedTemplateInfo = selectedTemplateKey
    ? templatesInfoMap[selectedTemplateKey]
    : undefined;
  if (chat && chat.templates && selectedTemplateKey) {
    selectedTemplateInfo = {
      ...templatesInfoMap[selectedTemplateKey],
      form: chat.template,
    };
  }
  useEffect(() => {
    if (templates && templates.length > 0) {
      setSelectedTemplateKey(templates[0]);
    }
  }, [templates]);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  /* istanbul ignore next */
  const handleResponse = (response: ISubmitSocketResponse) => {
    if (response.statusCode === 200) onDialogClose();
    setIsFormSubmitting(false);
  };
  // Without this the spinner runs forever on a silently dead socket: the
  // submit only ever cleared itself from the ack.
  const handleTimeout = () => {
    setIsFormSubmitting(false);
    enqueueSnackbar(tNs("templates.submitUnconfirmed"), {
      variant: "warning",
      autoHideDuration: 6000,
    });
  };
  const handleFormSubmit = (data: ITemplateFormData) => {
    setIsFormSubmitting(true);
    if (selectedTemplateInfo) {
      handleSubmit(
        data,
        selectedTemplateInfo,
        selectedRole,
        handleResponse,
        handleTimeout,
      );
    }
  };
  const handleFormChange = (
    _: React.SyntheticEvent,
    newValue: keyof typeof TEMPLATES,
  ) => {
    setSelectedTemplateKey(newValue);
  };

  return (
    <TemplateTabsContainer>
      {isTemplateLoading && <ProgressLoader />}
      {!isTemplateLoading && templates && templates.length === 0 && (
        <Typography>{tNs("templates.noTemplates")}</Typography>
      )}
      {!isTemplateLoading &&
        templates &&
        templates.length > 0 &&
        selectedTemplateKey && (
          <>
            <TemplateTabsStyle
              value={selectedTemplateKey}
              onChange={handleFormChange}
              variant="scrollable"
              scrollButtons={true}
              allowScrollButtonsMobile
            >
              {templates?.map(template => {
                const templateInfo =
                  templatesInfoMap[template as keyof typeof TEMPLATES];
                return (
                  <Tab
                    label={
                      <TemplateTabLabel variant="subtitle1">
                        {templateInfo.label}
                      </TemplateTabLabel>
                    }
                    value={template}
                    key={template}
                    style={{
                      textTransform: "none",
                    }}
                    data-testid="template-tabs"
                  />
                );
              })}
            </TemplateTabsStyle>
            <TabPanel
              value={selectedTemplateKey}
              index={selectedTemplateKey}
              uniqueKey={`template-${selectedTemplateKey}`}
            >
              {selectedTemplateInfo && (
                <TemplatePanel
                  templateInfo={selectedTemplateInfo}
                  handleFormSubmit={handleFormSubmit}
                  isFormSubmitting={isFormSubmitting}
                  chat={chat}
                  selectedRole={selectedRole}
                />
              )}
            </TabPanel>
          </>
        )}
    </TemplateTabsContainer>
  );
};

export default TemplateTabs;
